import type { FC } from "react";
import { FiInfo } from "react-icons/fi";
import { Link } from "react-router";
import { CANCEL_REFUND_GUIDE } from "../../../libs/mypage/cancelRefundText";
import { normalizeOrderCode } from "../../../libs/mypage/orderCode";
import { Container } from "../components/Container";
import { formatDate, formatYen, type OrderDetailDTO } from "../components/format";
import { MailDraftComposer } from "../components/MailDraftComposer";
import { Notice } from "../components/Notice";
import { PageHeading } from "../components/PageHeading";
import { SectionHeading } from "../components/SectionHeading";
import { useDecodedOrderCode } from "../hooks/useDecodedOrderCode";
import { useOrder } from "../hooks/useOrder";
import { buildCancelRequestMail } from "./cancelRequestMailto";

/**
 * キャンセルをご希望のお客様向けの案内。キャンセルはこの画面では実行せず、件名に
 * `【キャンセルリクエスト S12345】` を入れたメールで受け付ける (件名で機械的に判定して出荷を止め、
 * 手続きは担当者が代理ログインから行う)。
 *
 * 件名の文字列が無いと判定できず確認に時間がかかるので、宛先・件名・本文の下書きを画面に出し、
 * 項目ごとにコピーして送ってもらう。
 *
 * 注文詳細から直接は開かず、注文内容の変更のページから案内する (キャンセルの理由の多くは内容の変更で
 * 済むため)。戻る先も変更のページにする。
 */
export const CancelGuidePage: FC = () => {
  const code = useDecodedOrderCode();
  const result = useOrder(code);

  if (result.status === "loading") {
    return (
      <Container>
        <p className="py-8 text-center text-neutral-500 text-sm">読み込み中…</p>
      </Container>
    );
  }
  if (result.status === "not-found") {
    return (
      <Container>
        <PageHeading title="ご注文が見つかりませんでした" />
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }
  if (result.status === "error") {
    return (
      <Container>
        <PageHeading title="注文情報を取得できませんでした" description={result.message} />
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }

  const order = result.order;
  if (!order.isCancelable) {
    return (
      <Container>
        <p className="mb-4 text-neutral-500 text-xs">注文番号 {order.code}</p>
        <PageHeading
          title="この注文はキャンセルできません"
          description="発送の準備に入っている、またはすでにキャンセル済み・発送済みのご注文です。発送済みのご注文は、お受け取り後に返品のお手続きをご利用ください。"
        />
        <Link
          to={`/orders/${normalizeOrderCode(order.code)}`}
          className="text-sm underline underline-offset-4"
        >
          注文詳細に戻る
        </Link>
      </Container>
    );
  }

  return <CancelGuideBody order={order} />;
};

const FLOW = [
  "ご依頼のメールが届きましたら、弊社より受付のメールをお送りし、ご注文の発送を一時的に保留いたします。",
  "弊社の担当者がご依頼の内容を確認し、キャンセルのお手続きをいたします。この間、お客様にお手続きいただくことはございません。",
  "お手続きが完了しましたら、弊社よりキャンセル完了のメールをお送りします。キャンセルをお受けできない場合も、弊社の担当者よりメールでご連絡いたします。",
];

export const CancelGuideBody: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  const slug = normalizeOrderCode(order.code);

  return (
    <Container>
      <Link
        to={`/orders/${slug}/change`}
        className="mb-6 inline-block text-neutral-500 text-xs hover:text-neutral-900"
      >
        ← 注文内容の変更
      </Link>
      <PageHeading
        title="ご注文のキャンセルについて"
        description="キャンセルをご依頼いただく前に、このページを最後までお読みください。キャンセルをお受けできない場合や、ご返金の方法、ご依頼のメールの作り方をご案内しています。"
      />

      <SectionHeading title="対象の注文" />
      <dl className="mb-8 divide-y divide-neutral-200 text-sm">
        <div className="flex items-baseline justify-between gap-3 py-3">
          <dt className="text-neutral-500">注文番号</dt>
          <dd className="font-medium text-lg text-neutral-900">{order.code.replace(/^#/, "")}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 py-3">
          <dt className="text-neutral-500">注文日</dt>
          <dd className="text-neutral-900">{formatDate(order.createdAt)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 py-3">
          <dt className="text-neutral-500">合計</dt>
          <dd className="text-neutral-900">{formatYen(order.totalPriceJpy)}</dd>
        </div>
      </dl>

      <SectionHeading title="ご依頼の前にご確認ください" />
      <ul className="mb-8 list-disc space-y-2 py-4 pl-5 text-neutral-700 text-sm leading-relaxed">
        <li>
          ご依頼のメールを確認するまでの間に発送の準備に入った場合や、すでに発送済みの場合は、キャンセルをお受けできません。その場合は、お受け取り後の返品
          (お客様のご都合による返品) としてのお手続きになります。
        </li>
        <li>
          キャンセルの理由によっては、お受けできない場合がございます。その場合は、弊社の担当者よりメールでご連絡いたします。
        </li>
        {order.isOrderChangeable && (
          <li>
            商品・色・サイズ・数量などを変えたいだけの場合は、キャンセルせずに
            <Link to={`/orders/${slug}/change`} className="underline underline-offset-4">
              注文内容の変更
            </Link>
            からご依頼いただけます。
          </li>
        )}
      </ul>

      <SectionHeading title="ご返金について" />
      <p className="pt-4 text-neutral-700 text-sm leading-relaxed">
        ご返金の方法は、ご注文時にお選びいただいたお支払い方法によって決まります。お支払い方法ごとのご返金の方法は次のとおりです。
      </p>
      <dl className="mb-8 space-y-3 py-4 text-sm leading-relaxed">
        {CANCEL_REFUND_GUIDE.map(({ method, body }) => (
          <div key={method}>
            <dt className="font-medium text-neutral-900">{method}</dt>
            <dd className="text-neutral-700">{body}</dd>
          </div>
        ))}
      </dl>

      <SectionHeading title="ご依頼の方法" />
      <div className="mb-8 space-y-5 py-4 text-neutral-700 text-sm leading-relaxed">
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            下記の宛先・件名・本文をコピーしてメールを作成し、本文の記入欄をご記入ください。キャンセルの理由のご記入は必須です。ご記入がない場合は、キャンセルのご依頼をお受けできません。
          </li>
          <li>
            件名は変更せずに、ご注文時のメールアドレスから送信してください。件名が変わっていたり、別のメールアドレスから届いたりすると、ご依頼の確認に時間がかかり、その間に発送の準備に入ってしまうことがあります。
          </li>
        </ol>
        <Notice variant="highlight" icon={<FiInfo size={16} />}>
          <p className="mb-1 font-medium">
            本文の【キャンセルの理由】と【ご注文情報】を必ずご記入ください。
          </p>
          <p>
            ご記入がないとご注文やご本人の確認ができず、お手続きまでにお時間をいただきます。その間に発送の準備に入ってしまうこともあります。
          </p>
        </Notice>
        <MailDraftComposer draft={buildCancelRequestMail(order.code)} />
      </div>

      <SectionHeading title="ご依頼のあとの流れ" />
      <ol className="mb-8 list-decimal space-y-2 py-4 pl-5 text-neutral-700 text-sm leading-relaxed">
        {FLOW.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </Container>
  );
};
